import React, { useState } from 'react';
import { Form, Input, Button, Checkbox, message, Tabs, Divider, Space } from 'antd';
import { GoogleOutlined, UserOutlined, LockOutlined, MailOutlined, BankOutlined, TeamOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useAuth } from '../../AuthProvider';
import './auth.css'; // Import the CSS file

const SignInScreen = () => {
  const { login, loginWithGoogle } = useAuth();
  const [tenantType, setTenantType] = useState('employer');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      await login(values.email, values.password, tenantType);
      message.success('Login successful!');
    } catch (error) {
      switch (error.code) {
        case 'auth/user-not-found':
          message.error('No user found with this email.');
          break;
        case 'auth/wrong-password':
          message.error('Incorrect password.');
          break;
        case 'auth/too-many-requests':
          message.error('Too many attempts. Please try again later.');
          break;
        default:
          message.error('Failed to login. Please try again.');
      }
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    try {
      await loginWithGoogle(tenantType);
      message.success('Login with Google successful!');
    } catch (error) {
      message.error('Failed to login with Google. Please try again.');
      console.error('Error:', error);
    } finally {
      setGoogleLoading(false);
    }
  };

  const tabItems = [
    {
      key: 'employer',
      label: 'Healthcare Facility',
    },
    {
      key: 'agency',
      label: 'Staffing Agency',
    },
  ];

  return (
    <div className="auth-container">
      <div className="auth-background">
        <div className="auth-background-overlay">
          <div className="auth-background-content">
            <h1>Welcome to VitalStaf</h1>
            <p>Connect healthcare professionals with opportunities</p>
            <div className="auth-features">
              <div className="feature-item">
                <div className="feature-icon">
                  <BankOutlined />
                </div>
                <span>Healthcare Facilities</span>
              </div>
              <div className="feature-item">
                <div className="feature-icon">
                  <UserOutlined />
                </div>
                <span>Medical Professionals</span>
              </div>
              <div className="feature-item">
                <div className="feature-icon">
                  <TeamOutlined />
                </div>
                <span>Staffing Agencies</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="auth-form-container">
        <div className="auth-form-wrapper">
          <div className="auth-header">
            <h2>Sign In</h2>
            <p>Welcome back! Please sign in to your account</p>
          </div>
          
          <div className="auth-tabs-container">
            <Tabs 
              defaultActiveKey="employer" 
              items={tabItems}
              onChange={setTenantType}
              centered
              className="auth-tabs"
            />
          </div>
          
          <Form name="login" onFinish={onFinish} layout="vertical" className="auth-form">
            <Form.Item 
              name="email" 
              label="Email Address"
              rules={[
                { required: true, message: 'Please enter your email!' },
                { type: 'email', message: 'Please enter a valid email!' }
              ]}
            >
              <Input 
                size="large" 
                placeholder="Enter your email address" 
                prefix={<MailOutlined />}
              />
            </Form.Item>
            
            <Form.Item 
              name="password" 
              label="Password"
              rules={[{ required: true, message: 'Please enter your password!' }]}
            >
              <Input.Password 
                size="large" 
                placeholder="Enter your password"
                prefix={<LockOutlined />}
              />
            </Form.Item>
            
            <Form.Item className="auth-form-options">
              <div className="auth-form-remember">
                <Checkbox>Remember me</Checkbox>
              </div>
              <Link className="auth-form-forgot" to="/forgot-password">
                Forgot password?
              </Link>
            </Form.Item>
            
            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit" 
                className="auth-form-button signin-button" 
                size="large"
                loading={loading}
                block
              >
                Sign In
              </Button>
            </Form.Item>
            
            <Divider className="auth-divider">
              <span>or continue with</span>
            </Divider>
            
            <Form.Item>
              <Button 
                type="default" 
                onClick={handleGoogleLogin} 
                className="auth-form-button google-button" 
                size="large"
                loading={googleLoading}
                block
                icon={<GoogleOutlined />}
              >
                Sign in with Google
              </Button>
            </Form.Item>
            
            <div className="auth-form-footer">
              Don't have an account?{' '}
              <Link to="/signup" className="auth-form-link">
                Sign up here
              </Link>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default SignInScreen;
